import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { ProducerFields } from "../lib/types";
import { display } from "../lib/format";
import { Card, Input, Empty, Spinner, ErrorNote } from "../components/ui";

const PAGE = 25;

export default function ProducerList() {
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);

  const { data, isLoading, error } = useQuery({
    queryKey: ["producers", q, offset],
    queryFn: () => api.producers.list({ q, offset, limit: PAGE }),
  });

  // The first four columns only. A generated table that renders forty fields
  // is unreadable on any screen, and the detail page is one click away.
  const columns = ProducerFields.slice(0, 4);

  return (
    <div>
      <header className="mb-4 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Producers</h1>
          <p className="text-sm text-muted">{data?.total ?? 0} total</p>
        </div>
        <div className="w-64">
          <Input
            value={q}
            onChange={(v) => {
              setQ(v);
              // Reset paging on a new query, or page 3 of the old results
              // shows an empty table for the new one.
              setOffset(0);
            }}
            placeholder="Search…"
          />
        </div>
      </header>

      {error && <ErrorNote error={error} />}
      {isLoading && <Spinner />}

      {!isLoading && data && data.items.length === 0 && (
        <Empty
          title="No producers yet"
          hint={q ? "No matches for that search." : "Rows will appear here once they are created."}
        />
      )}

      {!isLoading && data && data.items.length > 0 && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-hairline text-left text-muted">
                <tr>
                  {columns.map((c) => (
                    <th key={c.name} className="whitespace-nowrap px-4 py-3 font-medium">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} className="border-b border-hairline/60 last:border-0 hover:bg-hairline/20">
                    {columns.map((c, i) => (
                      <td key={c.name} className="whitespace-nowrap px-4 py-3">
                        {i === 0 ? (
                          <Link to={`/producers/${row.id}`} className="font-medium text-brand hover:underline">
                            {display((row as Record<string, unknown>)[c.name], c.type)}
                          </Link>
                        ) : (
                          display((row as Record<string, unknown>)[c.name], c.type)
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {data && data.total > PAGE && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <button
            className="rounded-card border border-hairline px-3 py-1.5 disabled:opacity-40"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
          >
            Previous
          </button>
          <span className="text-muted">
            {offset + 1}–{Math.min(offset + PAGE, data.total)} of {data.total}
          </span>
          <button
            className="rounded-card border border-hairline px-3 py-1.5 disabled:opacity-40"
            disabled={offset + PAGE >= data.total}
            onClick={() => setOffset(offset + PAGE)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
